import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/styles';
import axios from 'axios';
import SubCard from 'ui-component/cards/SubCard';
import CheckoutBackdrop from 'ui-component/loader/CheckoutBackdrop';
import HDConfirmationDialog from 'ui-component/dialog/HDConfirmationDialog';
import { useNotification } from 'store/context/Notification';
import { ERROR } from 'store/constant';
import { planUpgradeUrl } from 'api/hiredrateURLConstruct';
import PricingCard from './PricingCard';
import { Box, Typography, Stack } from '@mui/material';
import AppConfig from 'api/AppConfig';

const PlanUpgrade = () => {
    const { companyID } = useSelector((state) => state.account);
    const { showNotification } = useNotification();
    const configObject = AppConfig("APIConfig");

    const theme = useTheme();

    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingPlanData, setPendingPlanData] = useState(null);

    const handlePlanSelect = (plan) => {
        setPendingPlanData(plan);
        setShowConfirmDialog(true);
    };

    const handleConfirmRedirect = async () => {
        if (!pendingPlanData) return;
        try {
            setShowConfirmDialog(false);
            setCheckoutLoading(true);
            setSelectedPlan(pendingPlanData.name);

            const payload = {
                plan_id: pendingPlanData.plan_unique_id,
                company_id: companyID,
                quantity: 1,
                redirect_url:
                    window.location.origin +
                    "/settings/planupgrade/payment-success",
                cancel_url:
                    window.location.origin +
                    "/settings/planupgrade/payment-failed",
            };
            const response = await axios.post(planUpgradeUrl(), payload, {
                ...configObject,

                headers: {
                    ...configObject?.headers,
                    'Content-Type': 'application/json',
                },

            });

            if (response?.data?.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                setCheckoutLoading(false);
            }
        } catch (error) {
            const message = error?.response?.data?.detail || error?.message || 'Something went wrong';
            showNotification(ERROR, message, ERROR, 'Subscription');
            setCheckoutLoading(false);
            setPendingPlanData(null);
        }
    };

    const handleCancelRedirect = () => {
        setShowConfirmDialog(false);
        setPendingPlanData(null);
    };

    const getConfirmationMessage = () => {
        if (!pendingPlanData) return '';
        return `You are about to upgrade to the ${pendingPlanData.name} 
        plan at ${pendingPlanData.currency_symbol}${pendingPlanData.display_price}/${pendingPlanData.plan_cycle.toLowerCase()}.
        You will be redirected to Chargebee to complete the payment.`;
    };

    return (
        <>
            <HDConfirmationDialog
                open={showConfirmDialog}
                onClose={handleCancelRedirect}
                onConfirm={handleConfirmRedirect}
                title="Confirm Subscription Upgrade"
                message={getConfirmationMessage()}
                confirmButtonText="Proceed to Payment"
                cancelButtonText="Cancel"
                loading={checkoutLoading}
            >
                {pendingPlanData && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Plan Details:</Typography>
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{pendingPlanData.name}</Typography>
                            <Typography variant="body2" fontWeight={700} color="primary">
                                {pendingPlanData.currency_symbol}{pendingPlanData.display_price}/
                                {pendingPlanData.plan_cycle === 'Monthly' ? 'month' : 'year'}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Credits: {pendingPlanData.credits}</Typography>
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: theme.palette.warning.light + '20', borderRadius: 1, border: `1px solid ${theme.palette.warning.light}` }}>
                            <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                                You will be redirected to Chargebee to complete the payment.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </HDConfirmationDialog>

            <SubCard title="Subscription Upgrade" sx={{ background: theme.palette.background.paper, borderRadius: '18px' }}>
                <CheckoutBackdrop
                    open={checkoutLoading}
                    title="Redirecting to payment page..."
                    selectedItem={selectedPlan}
                    selectedItemLabel="Selected Plan"
                    redirectText="Redirecting to Chargebee..."
                    providerName="Chargebee"
                />
                <PricingCard onPlanSelect={handlePlanSelect}
                />
            </SubCard>
        </>
    );
};

export default PlanUpgrade;